import React, { useCallback } from 'react';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Box, Chip, Stack, Typography } from '@mui/material';

import { type Dayjs, formatDateSlashes } from '~/services/TimeService';

import { daysOfWeekMessage } from '~/utils/daysOfWeek';

import { getDateRangeAvailability } from '../../shared/dateRangeAvailability';
import { useFilteredByExpiry } from '../../shared/useFilteredByExpiry';
import { availabilityLabels, getAvailabilityChipColor } from '../surcharges/surchargeAvailability';
import { getMatchedCardSx } from '../surcharges/surchargeCardStyles';

import ManageRoomRateBlackoutDatesAction from './ManageRoomRateBlackoutDatesAction';
import type { RoomRateBlackoutAssignment } from './appliedBlackoutUtils';

interface Props {
  assignments: Array<RoomRateBlackoutAssignment>;
  hideEmptyState?: boolean;
  hideExpired: boolean;
  now: Dayjs;
}

export default function MatchedBlackoutRoomRateCards({ assignments, hideEmptyState = false, hideExpired, now }: Props) {
  const isExpired = useCallback(
    (assignment: RoomRateBlackoutAssignment) =>
      getDateRangeAvailability(assignment.roomBlackoutDate, now) === 'expired',
    [now],
  );
  const visibleAssignments = useFilteredByExpiry(assignments, isExpired, hideExpired);

  if (assignments.length === 0) {
    if (hideEmptyState) {
      return null;
    }

    return (
      <Box
        sx={(theme) => ({
          ...getMatchedCardSx(theme),
          color: 'text.secondary',
          py: 1.5,
          textAlign: 'center',
        })}
      >
        <Typography variant="body2">No matching room-rate blackout blocks.</Typography>
      </Box>
    );
  }

  if (visibleAssignments.length === 0) {
    if (hideEmptyState) {
      return null;
    }

    return (
      <Box
        sx={(theme) => ({
          ...getMatchedCardSx(theme),
          color: 'text.secondary',
          py: 1.5,
          textAlign: 'center',
        })}
      >
        <Typography variant="body2">All matched room-rate blackout blocks are expired.</Typography>
      </Box>
    );
  }

  return (
    <>
      {visibleAssignments.map((assignment) => {
        const availability = getDateRangeAvailability(assignment.roomBlackoutDate, now);
        const ratePlanLabel = [assignment.ratePlanName, assignment.ratePlanCode].filter(Boolean).join(' - ');

        return (
          <Box key={`${assignment.roomRateId}-${assignment.roomBlackoutDate.id}`} sx={getMatchedCardSx}>
            <Stack
              alignItems={{ xs: 'flex-start', md: 'center' }}
              direction={{ xs: 'column', md: 'row' }}
              flexWrap="wrap"
              gap={1.5}
              rowGap={1}
            >
              <Stack alignItems="center" direction="row" spacing={0.75} sx={{ color: 'success.dark', flexShrink: 0 }}>
                <CheckCircleOutlineIcon fontSize="small" />
                <Typography fontWeight={700} variant="body2">
                  Matched
                </Typography>
              </Stack>
              <Stack alignItems="center" direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                <Typography fontWeight={700} variant="body2">
                  {assignment.roomName}
                </Typography>
                <ManageRoomRateBlackoutDatesAction href={assignment.href} />
              </Stack>
              <Typography color="text.secondary" sx={{ minWidth: 0 }} variant="body2">
                {ratePlanLabel} · {formatDateSlashes(assignment.roomBlackoutDate.start_date)} to{' '}
                {formatDateSlashes(assignment.roomBlackoutDate.end_date)} ·{' '}
                {daysOfWeekMessage(assignment.roomBlackoutDate)}
              </Typography>
              <Chip
                color={getAvailabilityChipColor(availability)}
                label={availabilityLabels[availability]}
                size="small"
                sx={{ flexShrink: 0 }}
                variant="outlined"
              />
            </Stack>
          </Box>
        );
      })}
    </>
  );
}
